import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Helper function to convert all numeric values to floats in workout data
const convertToFloats = (data) => {
  if (!data) return data

  // Use the brute force approach directly - it's more reliable
  return JSON.parse(
    JSON.stringify(data, (key, value) => {
      if (typeof value === 'number' && Number.isInteger(value)) {
        return parseFloat(value)
      }
      return value
    }),
  )
}

// Async thunks
export const fetchWorkoutDrafts = createAsyncThunk(
  'workoutDrafts/fetchWorkoutDrafts',
  async (
    {
      token,
      profileId,
      filters = {},
      page = 0,
      size = 20,
      sortBy = 'lastModified',
      sortDir = 'desc',
    },
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy,
        sortDir,
        ...filters,
      })

      const response = await apiCallWithHost(
        `/v2/drafts?${queryParams}`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch workout drafts')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const createWorkoutDraft = createAsyncThunk(
  'workoutDrafts/createWorkoutDraft',
  async ({ token, profileId, draftData }, { rejectWithValue }) => {
    try {
      // Convert all numeric values to floats before sending
      const convertedDraftData = convertToFloats(draftData)

      // BRUTE FORCE: Convert any remaining integers to floats by stringifying and parsing
      const finalData = JSON.parse(
        JSON.stringify(convertedDraftData, (key, value) => {
          if (typeof value === 'number' && Number.isInteger(value)) {
            console.log(`Converting integer ${value} to float ${value}.0 for key: ${key}`)
            return parseFloat(value)
          }
          return value
        }),
      )

      const response = await apiCallWithHost(
        `/v2/drafts`,
        {
          method: 'POST',
          body: JSON.stringify(finalData),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to create workout draft')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const updateWorkoutDraft = createAsyncThunk(
  'workoutDrafts/updateWorkoutDraft',
  async ({ token, profileId, id, draftData }, { rejectWithValue }) => {
    try {
      // Convert all numeric values to floats before sending
      const convertedDraftData = convertToFloats(draftData)

      // Debug: Log the data before and after conversion
      console.log('Original draftData:', JSON.stringify(draftData, null, 2))
      console.log('Converted draftData:', JSON.stringify(convertedDraftData, null, 2))

      // BRUTE FORCE: Convert any remaining integers to floats by stringifying and parsing
      const finalData = JSON.parse(
        JSON.stringify(convertedDraftData, (key, value) => {
          if (typeof value === 'number' && Number.isInteger(value)) {
            console.log(`Converting integer ${value} to float ${value}.0 for key: ${key}`)
            return parseFloat(value)
          }
          return value
        }),
      )

      console.log('Final data being sent:', JSON.stringify(finalData, null, 2))

      // LAST RESORT: Log the actual JSON string being sent
      const jsonString = JSON.stringify(finalData)
      console.log('ACTUAL JSON STRING BEING SENT:', jsonString)

      // Check if the JSON string contains integers
      if (jsonString.includes('"WEIGHT": 185')) {
        console.log('❌ JSON STILL CONTAINS INTEGER 185!')
      } else if (jsonString.includes('"WEIGHT": 185.0')) {
        console.log('✅ JSON CONTAINS FLOAT 185.0!')
      }

      const response = await apiCallWithHost(
        `/v2/drafts/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(finalData),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update workout draft')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const fetchWorkoutDraft = createAsyncThunk(
  'workoutDrafts/fetchWorkoutDraft',
  async ({ token, profileId, id }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/drafts/${id}`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch workout draft')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const deleteWorkoutDraft = createAsyncThunk(
  'workoutDrafts/deleteWorkoutDraft',
  async ({ token, profileId, id }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/drafts/${id}`,
        {
          method: 'DELETE',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete workout draft')
      }

      return { id }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const validateWorkoutDraft = createAsyncThunk(
  'workoutDrafts/validateWorkoutDraft',
  async ({ token, profileId, id }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/drafts/${id}/validate`,
        {
          method: 'POST',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to validate workout draft')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

export const publishWorkoutDraft = createAsyncThunk(
  'workoutDrafts/publishWorkoutDraft',
  async ({ token, profileId, id }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/drafts/${id}/publish`,
        {
          method: 'POST',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to publish workout draft')
      }

      const data = await response.json()
      return { id, publishedWorkout: data }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

const initialState = {
  drafts: [],
  currentDraft: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  pagination: {
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
    totalElements: 0,
  },
  filters: {
    namePattern: '',
    tag: '',
    visibility: '',
    templatesOnly: false,
  },
  sortBy: 'lastModified',
  sortDir: 'desc',
}

const workoutDraftsSlice = createSlice({
  name: 'workoutDrafts',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.pageNumber = 0 // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {
        namePattern: '',
        tag: '',
        visibility: '',
        templatesOnly: false,
      }
      state.pagination.pageNumber = 0
    },
    setPage: (state, action) => {
      state.pagination.pageNumber = action.payload
    },
    setSorting: (state, action) => {
      state.sortBy = action.payload.sortBy
      state.sortDir = action.payload.sortDir
      state.pagination.pageNumber = 0
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch drafts
      .addCase(fetchWorkoutDrafts.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchWorkoutDrafts.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.drafts = action.payload.content || []
        state.pagination = {
          pageNumber: action.payload.number || 0,
          pageSize: action.payload.size || 20,
          totalPages: action.payload.totalPages || 0,
          totalElements: action.payload.totalElements || 0,
        }
      })
      .addCase(fetchWorkoutDrafts.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })

      // Create draft
      .addCase(createWorkoutDraft.fulfilled, (state, action) => {
        state.drafts.unshift(action.payload)
        state.pagination.totalElements += 1
      })

      // Update draft
      .addCase(updateWorkoutDraft.fulfilled, (state, action) => {
        const index = state.drafts.findIndex((draft) => draft.id === action.payload.id)
        if (index !== -1) {
          state.drafts[index] = action.payload
        }

        // Also update the current draft if it's the one being updated
        if (state.currentDraft && state.currentDraft.id === action.payload.id) {
          state.currentDraft = action.payload
        }
      })

      // Delete draft
      .addCase(deleteWorkoutDraft.fulfilled, (state, action) => {
        state.drafts = state.drafts.filter((draft) => draft.id !== action.payload.id)
        state.pagination.totalElements = Math.max(0, state.pagination.totalElements - 1)
      })

      // Validate draft
      .addCase(validateWorkoutDraft.fulfilled, (state, action) => {
        const index = state.drafts.findIndex((draft) => draft.id === action.payload.id)
        if (index !== -1) {
          state.drafts[index] = action.payload
        }
      })

      // Publish draft
      .addCase(publishWorkoutDraft.fulfilled, (state, action) => {
        // Remove the draft from the list since it's now published
        state.drafts = state.drafts.filter((draft) => draft.id !== action.payload.id)
        state.pagination.totalElements = Math.max(0, state.pagination.totalElements - 1)
        // DON'T clear currentDraft immediately - let the user interact with the success modal first
        // The draft will remain visible until the user navigates away or closes the modal
      })

      // Fetch single draft
      .addCase(fetchWorkoutDraft.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchWorkoutDraft.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.currentDraft = action.payload
        state.error = null
      })
      .addCase(fetchWorkoutDraft.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload
      })
  },
})

// Actions
export const { setFilters, clearFilters, setPage, setSorting, clearError } =
  workoutDraftsSlice.actions

// Selectors
export const selectWorkoutDrafts = (state) => state.workoutDrafts.drafts
export const selectWorkoutDraftsStatus = (state) => state.workoutDrafts.status
export const selectWorkoutDraftsError = (state) => state.workoutDrafts.error
export const selectWorkoutDraftsPagination = (state) => state.workoutDrafts.pagination
export const selectWorkoutDraftsFilters = (state) => state.workoutDrafts.filters
export const selectWorkoutDraftsSorting = (state) => ({
  sortBy: state.workoutDrafts.sortBy,
  sortDir: state.workoutDrafts.sortDir,
})

// Single draft selectors
export const selectWorkoutDraft = (state) => state.workoutDrafts.currentDraft
export const selectWorkoutDraftStatus = (state) => state.workoutDrafts.status
export const selectWorkoutDraftError = (state) => state.workoutDrafts.error

export default workoutDraftsSlice.reducer
